import { BooleanFilter, ReferenceFilter, SelectFilter, TextFilter } from 'elements/filtering/filters'
import type { FilterConfig, OrderOption, SortOption, SuggestedFilter } from 'elements/filtering/types'
import { ConditionalTextFilter, DateFilter, Priority, ProjectBrandFilter, SystemSize } from './filters'
import StatusFilter from './StatusFilter'

export const ORDER_BY_PROJECT_SORT_OPTIONS: SortOption[] = [
  { value: 'system_sold_id', label: 'Has Sold System' },
  { value: 'installation_date', label: 'Install Date' },
  { value: 'id', label: 'Project Creation Date' },
  { value: 'has_orders', label: 'Status' },
]

export const ORDER_BY_PROJECT_ORDER_OPTIONS: OrderOption[] = [
  { value: 'asc', label: 'Ascending' },
  { value: 'desc', label: 'Descending' },
]

const parseDateValues = (
  fields: { afterField: string; beforeField: string; onField: string; isBeforeField: string; isAfterField: string },
  filterValues: Record<string, unknown>
): Record<string, unknown> => {
  if (filterValues[fields.afterField] !== undefined) {
    return { conditional: 0, date: filterValues[fields.afterField] }
  }
  if (filterValues[fields.beforeField] !== undefined) {
    return { conditional: 1, date: filterValues[fields.beforeField] }
  }
  if (filterValues[fields.onField] !== undefined) {
    return { conditional: 2, date: filterValues[fields.onField] }
  }
  if (filterValues[fields.isBeforeField] !== undefined) {
    return { conditional: 3, date: filterValues[fields.isBeforeField] }
  }
  if (filterValues[fields.isAfterField] !== undefined) {
    return { conditional: 4, date: filterValues[fields.isAfterField] }
  }
  return {}
}

const parseBaseValue = (fieldKey: string, filterValues: Record<string, unknown>): Record<string, unknown> => {
  const value = filterValues[fieldKey] as string
  if (!value) return {}
  return {
    conditional: !value.startsWith('-'),
    query: value.replace('-', ''),
  }
}

const parseDoubleRef = (filterValues: Record<string, unknown>, trueField: string, falseField: string) => {
  return {
    conditional: Object.keys(filterValues).includes(trueField),
    itemId: filterValues[trueField] || filterValues[falseField],
  }
}

export const ORDER_BY_PROJECT_FILTERS: FilterConfig[] = [
  {
    id: 'assigned_role_id',
    name: 'Assigned',
    field: ReferenceFilter,
    props: {
      reference: 'roles',
      optionText: 'display',
      optionValue: 'id',
      perPage: 1000,
    },
  },
  {
    id: 'locality',
    name: 'City',
    field: ConditionalTextFilter,
    parseInitialValue: (filterValues) => parseBaseValue('locality', filterValues as Record<string, unknown>),
  },
  {
    id: 'dateCreated',
    name: 'Date Created',
    field: DateFilter,
    relatedFields: ['created_date_gte', 'created_date_lte', 'created_date_eq', 'created_date_lt', 'created_date_gt'],
    parseInitialValue: (filterValues) =>
      parseDateValues(
        {
          afterField: 'created_date_gte',
          beforeField: 'created_date_lte',
          onField: 'created_date_eq',
          isBeforeField: 'created_date_lt',
          isAfterField: 'created_date_gt',
        },
        filterValues as Record<string, unknown>
      ),
  },
  {
    id: 'dateSold',
    name: 'Date Sold',
    field: DateFilter,
    relatedFields: ['sold_on_date_gte', 'sold_on_date_lte', 'sold_on_date_eq', 'sold_on_date_lt', 'sold_on_date_gt'],
    parseInitialValue: (filterValues) =>
      parseDateValues(
        {
          afterField: 'sold_on_date_gte',
          beforeField: 'sold_on_date_lte',
          onField: 'sold_on_date_eq',
          isBeforeField: 'sold_on_date_lt',
          isAfterField: 'sold_on_date_gt',
        },
        filterValues as Record<string, unknown>
      ),
  },
  {
    id: 'zips',
    name: 'Postcode',
    field: ConditionalTextFilter,
    parseInitialValue: (filterValues) => parseBaseValue('zips', filterValues as Record<string, unknown>),
  },
  {
    id: 'priority',
    name: 'Priority',
    field: Priority,
    relatedFields: ['priority', 'priority_not'],
    parseInitialValue: (filterValues) =>
      parseDoubleRef(filterValues as Record<string, unknown>, 'priority', 'priority_not'),
  },
  {
    id: 'project_sold',
    name: 'Project Sold',
    field: BooleanFilter,
    props: {
      choices: [
        { id: true, name: 'Yes' },
        { id: false, name: 'No' },
      ],
    },
  },
  {
    id: 'starred',
    name: 'Starred',
    field: SelectFilter,
    props: {
      choices: [
        { id: 1, name: 'Yes' },
        { id: 0, name: 'No' },
      ],
    },
  },
  {
    id: 'states',
    name: 'State',
    field: ConditionalTextFilter,
    parseInitialValue: (filterValues) => parseBaseValue('states', filterValues as Record<string, unknown>),
  },
  {
    id: 'order_status',
    name: 'Status',
    field: StatusFilter,
    relatedFields: ['include_order_status', 'exclude_order_status'],
    parseInitialValue: (filterValues) => {
      const values = filterValues as Record<string, unknown>
      if (values['include_order_status']) {
        return { conditional: 'include', status: values['include_order_status'] }
      }
      if (values['exclude_order_status']) {
        return { conditional: 'exclude', status: values['exclude_order_status'] }
      }
      return {}
    },
  },
  {
    id: 'has_systems',
    name: 'System',
    field: BooleanFilter,
    props: {
      choices: [
        { id: true, name: 'Has systems' },
        { id: false, name: 'Does not have systems' },
      ],
    },
  },
  {
    id: 'systemSize',
    name: 'System Size',
    field: SystemSize,
    relatedFields: ['has_system_larger_than', 'has_system_smaller_than'],
    parseInitialValue: (filterValues) => {
      const values = filterValues as Record<string, unknown>
      return {
        conditional: Object.keys(values).includes('has_system_larger_than') ? 1 : 0,
        quantity: values.has_system_larger_than || values.has_system_smaller_than,
      }
    },
  },
  {
    id: 'manufacturer_name',
    name: 'Component Brand',
    field: ProjectBrandFilter,
  },
  {
    id: 'component_code',
    name: 'Component Code',
    field: TextFilter,
  },
  {
    id: 'component_type',
    name: 'Component Type',
    field: SelectFilter,
    props: {
      choices: [
        { id: 'module', name: 'Module' },
        { id: 'inverter', name: 'Inverter' },
        { id: 'battery', name: 'Battery' },
        { id: 'general', name: 'General' },
        { id: 'dc_optimizer', name: 'DC Optimizer' },
        { id: 'isolator', name: 'Isolator' },
        { id: 'meter', name: 'Meter' },
        { id: 'cable', name: 'Cable' },
        { id: 'battery_accessory', name: 'Battery Accessory' },
        { id: 'battery_inverter', name: 'Battery Inverter' },
        { id: 'inverter_accessory', name: 'Inverter Accessory' },
        { id: 'mounting_rail', name: 'Mounting Rail' },
        { id: 'mounting_roof_anchor', name: 'Mounting Roof Anchor' },
        { id: 'mounting_clamp', name: 'Mounting Clamp' },
        { id: 'mounting_flashing', name: 'Mounting Flashing' },
        { id: 'mounting_roof_fixing', name: 'Mounting Roof Fixing' },
        { id: 'mounting_other', name: 'Mounting Other' },
        { id: 'ev_charger', name: 'EV Charger' },
        { id: 'subscription', name: 'Subscription' },
        { id: 'heat_pump', name: 'Heat Pump' },
      ],
    },
  },
]

export const ORDER_BY_PROJECT_SUGGESTED_FILTERS: SuggestedFilter[] = [
  { id: 'zips', name: 'Postcode' },
  { id: 'starred', name: 'Starred' },
  { id: 'order_status', name: 'Status' },
  { id: 'manufacturer_name', name: 'Component Brand' },
  { id: 'component_type', name: 'Component Type' },
  { id: 'component_code', name: 'Component Code' },
]
